/**
 * Template Variables Editor Component
 * Editable form for template variables (lot, jurisdiction, dates, etc.)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card, Input, Button } from '@tracekit/core';
import { Save, RotateCcw, AlertCircle } from 'lucide-react';
import type { TemplateVariables } from '../types/template';

interface TemplateVariablesEditorProps {
  variables?: TemplateVariables;
  onSave?: (variables: TemplateVariables) => void;
  readOnly?: boolean;
}

export function TemplateVariablesEditor({
  variables,
  onSave,
  readOnly = false,
}: TemplateVariablesEditorProps) {
  const [editedVariables, setEditedVariables] = useState<TemplateVariables>(
    variables || {
      target_lot_stream: '[Enter target lot/stream/track]',
      jurisdiction: '[Enter jurisdiction]',
      proposal_date: '[Enter date]',
      prime_bidder: '[Enter name]',
      proposal_contact: '[Enter name]',
      submission_platform: '[Enter portal/email/instructions]',
      pricing_envelope_rules: '[Enter rules]',
      page_limits: '[Enter limits]',
      formatting_constraints: '[Enter formatting requirements]',
    }
  );

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (variables) {
      setEditedVariables(variables);
    }
  }, [variables]);

  const handleChange = (key: keyof TemplateVariables, value: string) => {
    setEditedVariables(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (onSave) {
      onSave(editedVariables);
      setHasChanges(false);
    }
  };

  const handleReset = () => {
    if (variables) {
      setEditedVariables(variables);
      setHasChanges(false);
    }
  };

  const hasPlaceholders = useMemo(() => {
    return Object.values(editedVariables).some(v => 
      v.includes('[Enter') || v.includes('[TBD]')
    );
  }, [editedVariables]);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">
            Template Variables (Fill-in-the-Blank)
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            These variables will be used throughout the proposal template
          </p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            {hasChanges && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReset}
                  className="flex items-center gap-1"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSave}
                  className="flex items-center gap-1"
                >
                  <Save className="w-4 h-4" />
                  Save Changes
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {hasPlaceholders && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                Incomplete Variables
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                Some variables have placeholder values ([Enter...] or [TBD]). Fill them in for accurate template generation.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="target_lot_stream" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
            Target Lot / Stream / Track
          </label>
          <Input
            id="target_lot_stream"
            type="text"
            value={editedVariables.target_lot_stream}
            onChange={(e) => handleChange('target_lot_stream', e.target.value)}
            placeholder="e.g., Lot 1, Stream 2"
            readOnly={readOnly}
            className={isPlaceholder(editedVariables.target_lot_stream) ? 'border-yellow-300' : ''}
          />
        </div>

        <div>
          <label htmlFor="jurisdiction" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
            Jurisdiction / Governing Law
          </label>
          <Input
            id="jurisdiction"
            type="text"
            value={editedVariables.jurisdiction}
            onChange={(e) => handleChange('jurisdiction', e.target.value)}
            placeholder="e.g., Canada, British Columbia"
            readOnly={readOnly}
            className={isPlaceholder(editedVariables.jurisdiction) ? 'border-yellow-300' : ''}
          />
        </div>

        <div>
          <label htmlFor="proposal_date" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
            Proposal Date
          </label>
          <Input
            id="proposal_date"
            type="text"
            value={editedVariables.proposal_date}
            onChange={(e) => handleChange('proposal_date', e.target.value)}
            placeholder="e.g., January 15, 2024"
            readOnly={readOnly}
            className={isPlaceholder(editedVariables.proposal_date) ? 'border-yellow-300' : ''}
          />
        </div>

        <div>
          <label htmlFor="prime_bidder" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
            Prime Bidder / Consortium Lead
          </label>
          <Input
            id="prime_bidder"
            type="text"
            value={editedVariables.prime_bidder}
            onChange={(e) => handleChange('prime_bidder', e.target.value)}
            placeholder="e.g., Keystone Environmental Ltd."
            readOnly={readOnly}
            className={isPlaceholder(editedVariables.prime_bidder) ? 'border-yellow-300' : ''}
          />
        </div>

        <div>
          <label htmlFor="proposal_contact" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
            Proposal Contact / Authority
          </label>
          <Input
            id="proposal_contact"
            type="text"
            value={editedVariables.proposal_contact}
            onChange={(e) => handleChange('proposal_contact', e.target.value)}
            placeholder="e.g., John Doe, Project Manager"
            readOnly={readOnly}
            className={isPlaceholder(editedVariables.proposal_contact) ? 'border-yellow-300' : ''}
          />
        </div>

        <div>
          <Label htmlFor="submission_platform" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
            Submission Platform / Method
          </Label>
          <Input
            id="submission_platform"
            type="text"
            value={editedVariables.submission_platform}
            onChange={(e) => handleChange('submission_platform', e.target.value)}
            placeholder="e.g., Online portal, email to..."
            readOnly={readOnly}
            className={isPlaceholder(editedVariables.submission_platform) ? 'border-yellow-300' : ''}
          />
        </div>

        <div>
          <label htmlFor="pricing_envelope_rules" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
            Pricing Envelope Separation Rules
          </label>
          <Input
            id="pricing_envelope_rules"
            type="text"
            value={editedVariables.pricing_envelope_rules}
            onChange={(e) => handleChange('pricing_envelope_rules', e.target.value)}
            placeholder="e.g., Separate envelope, electronic submission"
            readOnly={readOnly}
            className={isPlaceholder(editedVariables.pricing_envelope_rules) ? 'border-yellow-300' : ''}
          />
        </div>

        <div>
          <label htmlFor="page_limits" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
            Allowed Page Limits
          </label>
          <Input
            id="page_limits"
            type="text"
            value={editedVariables.page_limits}
            onChange={(e) => handleChange('page_limits', e.target.value)}
            placeholder="e.g., 50 pages max, excluding appendices"
            readOnly={readOnly}
            className={isPlaceholder(editedVariables.page_limits) ? 'border-yellow-300' : ''}
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="formatting_constraints" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
            Formatting Constraints
          </label>
          <Input
            id="formatting_constraints"
            type="text"
            value={editedVariables.formatting_constraints}
            onChange={(e) => handleChange('formatting_constraints', e.target.value)}
            placeholder="e.g., 12pt font, 1 inch margins, single-spaced"
            readOnly={readOnly}
            className={isPlaceholder(editedVariables.formatting_constraints) ? 'border-yellow-300' : ''}
          />
        </div>
      </div>

      {hasChanges && !readOnly && (
        <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" size="sm" onClick={handleReset}>
            Discard Changes
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave}>
            Save Variables
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * Check if value is a placeholder
 */
function isPlaceholder(value: string): boolean {
  return value.includes('[Enter') || value.includes('[TBD]');
}
